import React, { useLayoutEffect, useRef, useState } from "react";
import { Box, Textarea } from "@mui/joy";
import { theme } from "styles/theme";
import { Markdown } from "./Markdown";
import type { AssistantStep, UserTurn } from "./types";

// 한 줄 초과인지 측정해서 접힘 가능 여부 결정. content 변경 시 재측정.
// useLayoutEffect 로 페인트 전에 측정·확정해, canCollapse 전환에 따른 pr(12→36) 변화가
// 화면에 보이는 시프트(텍스트 재줄바꿈·토글 좌측 점프)로 깜빡이지 않게 한다.
function useBubbleCollapse(content: string) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [canCollapse, setCanCollapse] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    // line-clamp 가 걸려 있어도 scrollHeight 는 full content 의 높이를 반환.
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
    setCanCollapse(el.scrollHeight > lineHeight * 1.5);
  }, [content]);
  return { isCollapsed, setIsCollapsed, canCollapse, contentRef };
}

// 버블 우측 상단의 접힘 토글 — 글자 첫 줄 중앙 라인에 정렬.
function BubbleCollapseToggle({
  isCollapsed,
  onToggle,
}: {
  isCollapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onToggle}
      aria-label={isCollapsed ? "버블 펼치기" : "버블 접기"}
      sx={{
        position: "absolute",
        // 버블 py(8) 부터 글자 한 줄 높이(1.6em) 만큼이 글자 첫 줄 영역.
        // 그 영역에 flex-center 로 icon 을 두면 텍스트 세로 중앙과 정확히 정렬됨.
        top: "4px",
        right: "4px",
        // hover 영역을 넉넉히 키워 누르기 쉽게.
        height: "28px",
        width: "28px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
        border: "none",
        cursor: "pointer",
        color: theme.vars.palette.Content.Weak,
        borderRadius: "8px",
        padding: 0,
        transition: "background-color 0.12s ease, color 0.12s ease",
        "&:hover": {
          color: theme.vars.palette.Content.Strong,
          backgroundColor: theme.vars.palette.Background.Level2,
        },
      }}
    >
      <span
        className="material-symbols-rounded"
        style={{ fontSize: "16px", lineHeight: 1 }}
      >
        {isCollapsed ? "expand_more" : "expand_less"}
      </span>
    </Box>
  );
}

// =========================
// UserBubble
// =========================

export function UserBubble({
  turn,
  editable,
  onEdit,
}: {
  turn: UserTurn;
  // 마지막 user turn 에서만 true — 인플레이스 수정(연필) 노출.
  editable?: boolean;
  // 수정 확정 시 호출 — 호출자가 활동지 복원 + rewind 재실행을 수행한다.
  onEdit?: (newMessage: string) => void;
}) {
  const { isCollapsed, setIsCollapsed, canCollapse, contentRef } =
    useBubbleCollapse(turn.content);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(turn.content);
  // 편집 진입 시 버블이 쪼그라들지 않게 현재 내용 너비를 측정해 최소 너비로 고정.
  const [editWidth, setEditWidth] = useState<number | undefined>(undefined);

  const startEdit = () => {
    setEditWidth(contentRef.current?.offsetWidth);
    setDraft(turn.content);
    setEditing(true);
  };
  const cancelEdit = () => {
    setDraft(turn.content);
    setEditing(false);
  };
  const saveEdit = () => {
    const v = draft.trim();
    // 빈/무변경이면 재실행 없이 닫기만.
    if (v && v !== turn.content) onEdit?.(v);
    setEditing(false);
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      saveEdit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: "4px",
        animation: "fadeInUp 0.25s ease",
        "@keyframes fadeInUp": {
          from: { opacity: 0, transform: "translateY(4px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
        // 연필은 평소 숨겼다가 버블 행 hover 시 노출.
        "&:hover .user-bubble-edit": { opacity: 1 },
      }}
    >
      {editable && !editing && (
        <Box
          component="button"
          type="button"
          className="user-bubble-edit"
          onClick={startEdit}
          aria-label="메시지 수정"
          title="메시지 수정"
          sx={{
            flexShrink: 0,
            // 버블이 2줄 이상(canCollapse)이면 좌측 상단 정렬, 1줄이면 세로 중앙.
            alignSelf: canCollapse ? "flex-start" : "center",
            mt: canCollapse ? "4px" : 0,
            opacity: 0,
            height: "28px",
            width: "28px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: theme.vars.palette.Content.Weak,
            borderRadius: "8px",
            padding: 0,
            transition:
              "opacity 0.12s ease, background-color 0.12s ease, color 0.12s ease",
            "&:hover": {
              color: theme.vars.palette.Content.Strong,
              backgroundColor: theme.vars.palette.Background.Level2,
            },
          }}
        >
          <span
            className="material-symbols-rounded"
            style={{ fontSize: "16px", lineHeight: 1 }}
          >
            edit
          </span>
        </Box>
      )}
      <Box
        sx={{
          maxWidth: "85%",
          position: "relative",
          px: "12px",
          py: "8px",
          pr: !editing && canCollapse ? "36px" : "12px",
          // 우측 정렬된 발신자(사용자) 버블 — 우측 하단에 꼬리
          borderRadius: "14px 14px 4px 14px",
          backgroundColor: theme.vars.palette.Surface.Forward,
          border: `1px solid ${theme.vars.palette.Border.Weak}`,
          color: theme.vars.palette.Content.Strong,
          fontSize: "13px",
          lineHeight: 1.5,
          wordBreak: "break-word",
        }}
      >
        {editing ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              minWidth: editWidth ? `${Math.max(editWidth, 200)}px` : "200px",
            }}
          >
            <Textarea
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              minRows={1}
              maxRows={8}
              variant="plain"
              sx={{
                fontSize: "13px",
                backgroundColor: "transparent",
                padding: 0,
                minHeight: "unset",
                "--Textarea-focusedHighlight": "transparent",
                "--Textarea-paddingBlock": "0px",
                "--Textarea-paddingInline": "0px",
                "&::before": { display: "none" },
              }}
            />
            <Box
              sx={{ display: "flex", justifyContent: "flex-end", gap: "6px" }}
            >
              <Box
                component="button"
                type="button"
                onClick={cancelEdit}
                sx={{
                  px: "8px",
                  py: "3px",
                  borderRadius: "6px",
                  border: `1px solid ${theme.vars.palette.Border.Weak}`,
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: "11px",
                  color: theme.vars.palette.Content.Medium,
                  "&:hover": {
                    backgroundColor: theme.vars.palette.Background.Level2,
                  },
                }}
              >
                취소
              </Box>
              <Box
                component="button"
                type="button"
                onClick={saveEdit}
                disabled={!draft.trim()}
                sx={{
                  px: "8px",
                  py: "3px",
                  borderRadius: "6px",
                  border: "none",
                  backgroundColor: theme.vars.palette.primary.solid,
                  color: theme.vars.palette.common.white,
                  cursor: "pointer",
                  fontSize: "11px",
                  fontWeight: 600,
                  "&:hover": { opacity: 0.9 },
                  "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
                }}
              >
                저장 후 재전송
              </Box>
            </Box>
          </Box>
        ) : (
          <>
            <Box
              ref={contentRef}
              sx={{
                whiteSpace: "pre-wrap",
                ...(isCollapsed && {
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  whiteSpace: "normal",
                }),
              }}
            >
              {turn.content}
            </Box>
            {canCollapse && (
              <BubbleCollapseToggle
                isCollapsed={isCollapsed}
                onToggle={() => setIsCollapsed((v) => !v)}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

// =========================
// TextStreamBubble
// =========================

export function TextStreamBubble({
  step,
  isLast,
  isStreaming,
}: {
  step: Extract<AssistantStep, { kind: "text" }>;
  isLast: boolean;
  isStreaming: boolean;
}) {
  const { isCollapsed, setIsCollapsed, canCollapse, contentRef } =
    useBubbleCollapse(step.content);
  // 스트리밍 중에는 클릭 토글을 잠금 (텍스트가 계속 자라는 동안 사용자가 의도치 않게 접지 않도록)
  const interactive = canCollapse && !isStreaming;
  return (
    <Box
      sx={{
        alignSelf: "flex-start",
        maxWidth: "85%",
        position: "relative",
        px: "12px",
        py: "8px",
        // 접기 chevron 거터를 항상 확보 — canCollapse 전환/스트리밍 종료 시 pr 변화로 인한
        // 레이아웃 시프트(텍스트 재줄바꿈·토글 점프)를 원천 차단.
        pr: "36px",
        // 좌측 정렬된 Agent 버블 — 좌측 하단에 꼬리
        borderRadius: "14px 14px 14px 4px",
        backgroundColor: theme.vars.palette.Surface.Forward,
        border: `1px solid ${theme.vars.palette.Border.Weak}`,
        fontSize: "13px",
        lineHeight: 1.6,
        color: theme.vars.palette.Content.Strong,
        wordBreak: "break-word",
        animation: "fadeIn 0.2s ease",
        "@keyframes fadeIn": {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      <Box
        ref={contentRef}
        sx={{
          whiteSpace: "pre-wrap",
          ...(isCollapsed &&
            interactive && {
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              whiteSpace: "normal",
            }),
        }}
      >
        {/* pr 36px 거터가 항상 있으므로 토글을 당겨 chevron 과 우측 정렬 (상태 무관 일정) */}
        <Markdown text={step.content} rawTogglePullRight="-24px" />
        {isLast && isStreaming && (
          <Box
            component="span"
            sx={{
              display: "inline-block",
              width: "6px",
              height: "13px",
              backgroundColor: theme.vars.palette.Content.Strong,
              verticalAlign: "text-bottom",
              ml: "2px",
              animation: "blink 1s steps(2) infinite",
              "@keyframes blink": {
                "0%, 50%": { opacity: 1 },
                "50.01%, 100%": { opacity: 0 },
              },
            }}
          />
        )}
      </Box>
      {interactive && (
        <BubbleCollapseToggle
          isCollapsed={isCollapsed}
          onToggle={() => setIsCollapsed((v) => !v)}
        />
      )}
    </Box>
  );
}

// =========================
// ReasoningBubble — 모델 추론(thinking) 요약. 버블이 아닌 흐릿한 인라인 블록.
// 스트리밍 중엔 펼쳐 보여주고, 완료되면 기본 접힘 (헤더만 남김).
// =========================

// 접기 헤더 — reasoning·tools·subagent 가 공유하는 동위계(peer) 헤더.
// 셰브론 90° 회전, 12px·Content.Weak. shimmer=true 면 라벨에 빛 sweep(진행 중 표시).
export function CollapseHeader({
  label,
  expanded,
  onToggle,
  shimmer = false,
}: {
  label: string;
  expanded: boolean;
  onToggle: () => void;
  shimmer?: boolean;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onToggle}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "2px",
        background: "transparent",
        border: "none",
        cursor: "pointer",
        // 좌측 라인을 ToolCallRow(px:8)와 맞춘다 — reasoning·tool 은 동위계 형제 스텝.
        padding: "2px 8px",
        // 헤더는 펼쳤을 때 나오는 도구 행(12px)보다 한 단계 크게 — 상위 묶음임을 표시.
        fontSize: "13px",
        color: theme.vars.palette.Content.Weak,
        "&:hover": { color: theme.vars.palette.Content.Medium },
      }}
    >
      <Box
        component="span"
        sx={
          shimmer
            ? {
                fontWeight: 600,
                backgroundImage: `linear-gradient(90deg, ${theme.vars.palette.Content.Weak} 0%, ${theme.vars.palette.Content.Weak} 35%, ${theme.vars.palette.Content.Strong} 50%, ${theme.vars.palette.Content.Weak} 65%, ${theme.vars.palette.Content.Weak} 100%)`,
                backgroundSize: "220% 100%",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
                WebkitTextFillColor: "transparent",
                animation: "agentShimmer 2.2s linear infinite",
                "@keyframes agentShimmer": {
                  from: { backgroundPosition: "220% 0" },
                  to: { backgroundPosition: "-220% 0" },
                },
              }
            : undefined
        }
      >
        {label}
      </Box>
      <span
        className="material-symbols-rounded"
        style={{
          fontSize: "16px",
          lineHeight: 1,
          transform: expanded ? "rotate(90deg)" : "none",
          transition: "transform 0.15s ease",
        }}
      >
        chevron_right
      </span>
    </Box>
  );
}

export function ReasoningBubble({
  step,
  isStreaming,
}: {
  step: Extract<AssistantStep, { kind: "reasoning" }>;
  isStreaming: boolean;
}) {
  // 기본 접힘 — 추론은 reasoning 라벨을 눌러 펼친다.
  const [expanded, setExpanded] = useState(false);

  return (
    <Box sx={{ alignSelf: "flex-start", maxWidth: "85%" }}>
      <CollapseHeader
        label="reasoning"
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        shimmer={isStreaming}
      />
      {expanded && (
        <Box
          sx={{
            whiteSpace: "pre-wrap",
            fontSize: "12px",
            lineHeight: 1.6,
            fontStyle: "italic",
            color: theme.vars.palette.Content.Weak,
            pl: "4px",
            borderLeft: `2px solid ${theme.vars.palette.Border.Weak}`,
            ml: "8px",
            wordBreak: "break-word",
          }}
        >
          {step.content}
          {isStreaming && (
            <Box
              component="span"
              sx={{
                display: "inline-block",
                width: "5px",
                height: "12px",
                backgroundColor: theme.vars.palette.Content.Weak,
                verticalAlign: "text-bottom",
                ml: "2px",
                animation: "blink 1s steps(2) infinite",
                "@keyframes blink": {
                  "0%, 50%": { opacity: 1 },
                  "50.01%, 100%": { opacity: 0 },
                },
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
